import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Heart, User, Menu, ChevronDown, Store, Package, LogOut, Settings, Search, X, MoreHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import SearchBox from "./SearchBox";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

const Header = ({ onSearch }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { items: cartItems, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [vehicleBrands, setVehicleBrands] = useState([]);
  const [showMoreBrands, setShowMoreBrands] = useState(false);
  const [guestCart, setGuestCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(14);
  const navRef = useRef(null);
  
  // Hover catalog state
  const [hoveredBrand, setHoveredBrand] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogCache = useRef({});
  const hoverTimeout = useRef(null);
  const dropdownRef = useRef(null);

  const aracMatch = location.pathname.match(/^\/arac\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?/);
  const activeVehicleBrand = aracMatch ? aracMatch[1] : null;

  useEffect(() => {
    const loadGuestCart = () => {
      const saved = localStorage.getItem('pazarlo_guest_cart');
      if (saved) setGuestCart(JSON.parse(saved));
    };
    loadGuestCart();
    const interval = setInterval(loadGuestCart, 500);
    window.addEventListener('storage', loadGuestCart);
    return () => { clearInterval(interval); window.removeEventListener('storage', loadGuestCart); };
  }, []);

  const cartItemCount = isAuthenticated ? totalItems : guestCart.reduce((sum, item) => sum + item.quantity, 0);
  const guestCartTotal = guestCart.reduce((sum, item) => sum + (item.price || item.display_price || 0) * item.quantity, 0);
  const displayCartTotal = isAuthenticated ? totalPrice : guestCartTotal;
  const displayCartItems = isAuthenticated ? cartItems : guestCart;
  
  const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

  const removeFromGuestCart = (productId) => {
    const newCart = guestCart.filter(item => item.id !== productId);
    localStorage.setItem('pazarlo_guest_cart', JSON.stringify(newCart));
    setGuestCart(newCart);
  };

  useEffect(() => {
    axios.get(API + "/api/vehicles/brands").then(r => setVehicleBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (navRef.current) {
        const width = navRef.current.offsetWidth;
        setVisibleCount(Math.max(6, Math.min(Math.floor((width - 60) / 105), 18)));
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [vehicleBrands]);

  const handleLogout = () => { logout(); navigate("/"); };

  // Brand hover catalog
  const handleBrandHover = useCallback((brandSlug) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(async () => {
      setHoveredBrand(brandSlug);
      if (catalogCache.current[brandSlug]) {
        setCatalogItems(catalogCache.current[brandSlug]);
        return;
      }
      setCatalogLoading(true);
      try {
        const { data } = await axios.get(API + "/api/vehicles/brands/" + brandSlug + "/catalog");
        catalogCache.current[brandSlug] = data;
        setCatalogItems(data);
      } catch { setCatalogItems([]); }
      setCatalogLoading(false);
    }, 150);
  }, []);

  const handleBrandLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHoveredBrand(null);
      setCatalogItems([]);
    }, 200);
  }, []);

  const handleDropdownEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredBrand(null);
      setCatalogItems([]);
    }, 150);
  }, []);

  const handleCatalogClick = (item) => {
    setHoveredBrand(null);
    setCatalogItems([]);
    if (item.year_slug) {
      navigate("/arac/" + item.brand_slug + "/" + item.model_slug + "/" + item.year_slug);
    } else {
      navigate("/arac/" + item.brand_slug + "/" + item.model_slug);
    }
  };

  const prioritySlugs = ["opel", "chevrolet", "bmw", "mercedes-benz", "volkswagen", "audi", "seat", "skoda", "renault", "peugeot", "citroen", "ford", "toyota", "hyundai", "honda", "fiat", "kia", "nissan", "dacia", "volvo"];
  const sortedBrands = [
    ...prioritySlugs.map(slug => vehicleBrands.find(b => b.slug === slug)).filter(Boolean),
    ...vehicleBrands.filter(b => !prioritySlugs.includes(b.slug))
  ];
  const visibleBrands = sortedBrands.slice(0, visibleCount);
  const overflowBrands = sortedBrands.slice(visibleCount);

  return (
    <header className="sticky-header border-b">
      {mobileSearchOpen && (
        <div className="fixed inset-0 bg-white z-[9999] md:hidden">
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-3 p-4 border-b bg-white">
              <Button variant="ghost" size="icon" onClick={() => setMobileSearchOpen(false)} data-testid="close-mobile-search">
                <X className="h-6 w-6" />
              </Button>
              <div className="flex-1">
                <SearchBox className="w-full" autoFocus={true} onSearchComplete={() => setMobileSearchOpen(false)} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-900 text-white py-2 px-4 text-sm hidden md:block">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span>Türkiye&#39;nin Oto Yedek Parça Pazarı</span>
            <span className="text-orange-400">|</span>
            <span>Güvenli Alışveriş</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.user_type === "admin" ? (
              <Link to="/admin" className="bg-orange-500 text-white px-3 py-1 rounded-lg hover:bg-orange-600 transition-colors font-semibold">Yönetim Paneli</Link>
            ) : (
              <Link to="/seller" className="hover:text-orange-400 transition-colors">Satıcı Ol</Link>
            )}
            <span>|</span>
            <span>Yardım</span>
          </div>
        </div>
      </div>

      <div className="bg-white py-3 px-4">
        <div className="container mx-auto flex items-center gap-2 md:gap-4">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <div className="py-4">
                <h3 className="font-bold text-lg mb-4">Kategoriler</h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div key={cat.id}>
                      <Link to={"/products?category=" + cat.id} className="block py-2 px-3 rounded-lg hover:bg-orange-50 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>{cat.name} ({cat.product_count})</Link>
                      {cat.children && cat.children.length > 0 && (
                        <div className="ml-4 space-y-1">
                          {cat.children.map((child) => (
                            <Link key={child.id} to={"/products?category=" + child.id} className="block py-1 px-3 text-sm text-slate-600 hover:bg-orange-50 rounded" onClick={() => setMobileMenuOpen(false)}>{child.name} ({child.product_count})</Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <hr className="my-4" />
                <h3 className="font-bold text-lg mb-3">Araç Markaları</h3>
                <div className="space-y-1">
                  {sortedBrands.map((brand) => (
                    <Link key={brand.id} to={"/arac/" + brand.slug} className={"block py-2 px-3 rounded-lg transition-colors font-medium text-sm " + (activeVehicleBrand === brand.slug ? "bg-orange-50 text-orange-600" : "text-slate-700 hover:bg-orange-50")} onClick={() => setMobileMenuOpen(false)}>{brand.name}</Link>
                  ))}
                </div>
                <hr className="my-4" />
                {!isAuthenticated ? (
                  <div className="space-y-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}><Button className="w-full" variant="outline">Giriş Yap</Button></Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}><Button className="w-full bg-orange-500 hover:bg-orange-600">Kayıt Ol</Button></Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)}><Button className="w-full" variant="outline">Profilim</Button></Link>
                    {user?.user_type === "seller" && (
                      <Link to="/seller/dashboard" className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-orange-50 font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="seller-dashboard-link"><Store className="h-4 w-4" />Satıcı Paneli</Link>
                    )}
                    {user?.user_type === "admin" && (
                      <>
                        <Link to="/admin" className="flex items-center gap-2 text-orange-600 font-semibold py-2 px-3 rounded-lg hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)} data-testid="admin-dashboard-link"><Settings className="h-4 w-4" />Yönetim Paneli</Link>
                        <Link to="/sistem-yonetimi" className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}><Package className="h-4 w-4" />Sistem Yönetimi</Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <a href="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0" data-testid="logo-link" onClick={(e) => { e.preventDefault(); window.location.href = "/"; }}>
            <div className="w-9 h-9 md:w-10 md:h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Settings className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-bold text-xl text-slate-900">Pazarlo</h1>
              <p className="text-xs text-slate-500">Oto Yedek Parça Pazarı</p>
            </div>
          </a>

          <SearchBox className="hidden md:block flex-1 max-w-2xl mx-4" />

          <Button variant="ghost" size="icon" className="md:hidden flex-1 justify-start px-3 py-2 h-10 bg-slate-100 rounded-full" onClick={() => setMobileSearchOpen(true)} data-testid="mobile-search-trigger">
            <Search className="h-5 w-5 text-slate-400 mr-2" />
            <span className="text-slate-400 text-sm truncate">Ürün, OEM ara...</span>
          </Button>

          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {isAuthenticated ? (
              <>
                <Link to="/favorites" data-testid="favorites-link" className="hidden sm:block">
                  <Button variant="ghost" size="icon" className="relative"><Heart className="h-6 w-6" /></Button>
                </Link>
                <DropdownMenu open={cartOpen} onOpenChange={setCartOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative" data-testid="cart-btn">
                      <ShoppingCart className="h-6 w-6" />
                      {cartItemCount > 0 && <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500">{cartItemCount}</Badge>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80">
                    <div className="p-3 border-b"><h3 className="font-semibold">Sepetim ({cartItemCount})</h3></div>
                    {cartItemCount === 0 ? (
                      <div className="p-6 text-center text-slate-500"><ShoppingCart className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p>Sepetiniz boş</p></div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        {displayCartItems.map(item => (
                          <div key={item.id} className="p-3 border-b flex gap-2 items-center">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.name || item.product_name}</p>
                              <p className="text-orange-600 font-semibold text-sm">{formatCurrency(item.price || item.display_price || 0)} x {item.quantity}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => removeFromGuestCart(item.id)} className="text-red-500 h-6 w-6 p-0">×</Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="p-3 border-t">
                      <div className="flex justify-between mb-2"><span className="text-slate-600">Toplam:</span><span className="font-bold text-orange-600">{formatCurrency(displayCartTotal)}</span></div>
                      <Link to="/cart" onClick={() => setCartOpen(false)}><Button className="w-full bg-orange-500 hover:bg-orange-600">Sepete Git</Button></Link>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-1 px-2 md:gap-2 md:px-4" data-testid="user-menu-btn">
                      <User className="h-5 w-5" /><span className="hidden md:inline">{user?.name?.split(" ")[0]}</span><ChevronDown className="h-4 w-4 hidden md:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild><Link to="/profile" className="flex items-center gap-2" data-testid="profile-link"><User className="h-4 w-4" />Profilim</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/orders" className="flex items-center gap-2" data-testid="orders-link"><Package className="h-4 w-4" />Siparişlerim</Link></DropdownMenuItem>
                    {user?.user_type === "seller" && <Link to="/seller/dashboard" className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-orange-50 font-medium" data-testid="seller-dashboard-link"><Store className="h-4 w-4" />Satıcı Paneli</Link>}
                    {user?.user_type === "admin" && (
                      <>
                        <Link to="/admin" className="flex items-center gap-2 text-orange-600 font-semibold py-2 px-3 rounded-lg hover:bg-orange-50" data-testid="admin-dashboard-link"><Settings className="h-4 w-4" />Yönetim Paneli</Link>
                        <Link to="/sistem-yonetimi" className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-orange-50"><Package className="h-4 w-4" />Sistem Yönetimi</Link>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Çıkış Yap</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-1 md:gap-2">
                <DropdownMenu open={cartOpen} onOpenChange={setCartOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative" data-testid="guest-cart-btn">
                      <ShoppingCart className="h-6 w-6" />
                      {cartItemCount > 0 && <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500">{cartItemCount}</Badge>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80">
                    <div className="p-3 border-b"><h3 className="font-semibold">Sepetim ({cartItemCount})</h3></div>
                    {cartItemCount === 0 ? (
                      <div className="p-6 text-center text-slate-500"><ShoppingCart className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p>Sepetiniz boş</p></div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        {displayCartItems.map(item => (
                          <div key={item.id} className="p-3 border-b flex gap-2 items-center">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.name || item.product_name}</p>
                              <p className="text-orange-600 font-semibold text-sm">{formatCurrency(item.price || item.display_price || 0)} x {item.quantity}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => removeFromGuestCart(item.id)} className="text-red-500 h-6 w-6 p-0">×</Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="p-3 border-t">
                      <div className="flex justify-between mb-2"><span className="text-slate-600">Toplam:</span><span className="font-bold text-orange-600">{formatCurrency(displayCartTotal)}</span></div>
                      <Link to="/guest-checkout?cart=true" onClick={() => setCartOpen(false)}><Button className="w-full bg-orange-500 hover:bg-orange-600">Satın Al</Button></Link>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/login" data-testid="login-link"><Button variant="ghost" size="sm" className="text-sm px-2 md:px-4">Giriş</Button></Link>
                <Link to="/register" className="hidden sm:block" data-testid="register-link"><Button className="bg-orange-500 hover:bg-orange-600" size="sm">Kayıt Ol</Button></Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Brand Navigation Bar */}
      <nav className="bg-white border-t shadow-sm hidden md:block relative" ref={navRef} data-testid="brand-nav">
        <div className="container mx-auto px-4 flex items-center">
          {visibleBrands.map((brand) => (
            <Link
              key={brand.id}
              to={"/arac/" + brand.slug}
              className={"text-[13px] font-bold uppercase tracking-wide px-3 py-3 transition-colors whitespace-nowrap " + (activeVehicleBrand === brand.slug || hoveredBrand === brand.slug ? "text-orange-500 border-b-2 border-orange-500 bg-slate-50" : "text-slate-800 hover:text-orange-500")}
              data-testid={"nav-brand-" + brand.slug}
              onMouseEnter={() => handleBrandHover(brand.slug)}
              onMouseLeave={handleBrandLeave}
            >
              {brand.name}
            </Link>
          ))}

          {overflowBrands.length > 0 && (
            <div className="relative ml-auto">
              <button onClick={() => setShowMoreBrands(!showMoreBrands)} className="flex items-center gap-1 text-[13px] font-bold text-slate-800 hover:text-orange-500 px-3 py-3 transition-colors" data-testid="more-brands-btn">
                <MoreHorizontal size={18} /><ChevronDown size={12} className={"transition-transform " + (showMoreBrands ? "rotate-180" : "")} />
              </button>
              {showMoreBrands && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreBrands(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border py-2 z-50 w-56 max-h-80 overflow-y-auto" data-testid="more-brands-dropdown">
                    {overflowBrands.map((brand) => (
                      <Link key={brand.id} to={"/arac/" + brand.slug} onClick={() => setShowMoreBrands(false)} className={"block px-4 py-2 text-sm font-semibold uppercase transition-colors " + (activeVehicleBrand === brand.slug ? "text-orange-500 bg-orange-50" : "text-slate-700 hover:text-orange-500 hover:bg-slate-50")} data-testid={"nav-more-brand-" + brand.slug}>{brand.name}</Link>
                    ))}
                    <div className="border-t mt-1 pt-1">
                      <Link to="/products" onClick={() => setShowMoreBrands(false)} className="block px-4 py-2 text-sm font-semibold text-slate-700 hover:text-orange-500 hover:bg-slate-50" data-testid="all-products-link">TÜM ÜRÜNLER</Link>
                      <Link to="/products?category=diger" onClick={() => setShowMoreBrands(false)} className="block px-4 py-2 text-sm font-semibold text-slate-700 hover:text-orange-500 hover:bg-slate-50" data-testid="other-products-link">DİĞER</Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Hover Catalog Dropdown */}
        {hoveredBrand && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full bg-white border-t shadow-2xl z-50"
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
            data-testid="brand-catalog-dropdown"
            style={{ maxHeight: "480px", overflowY: "auto" }}
          >
            <div className="container mx-auto px-4 py-4">
              {catalogLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : catalogItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400">Model bulunamadı</div>
              ) : (
                <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                  {catalogItems.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleCatalogClick(item)}
                      className="flex flex-col items-center text-center group cursor-pointer hover:bg-slate-50 rounded-lg p-2 transition-colors"
                      data-testid={"catalog-item-" + item.model_slug + "-" + (item.year_slug || "all")}
                    >
                      <div className="w-20 h-14 flex items-center justify-center mb-1">
                        <img
                          src={item.year_slug ? "/cars-thumb/" + item.brand_slug + "/" + item.model_slug + "-" + item.year_slug + ".webp" : "/cars-thumb/" + item.brand_slug + "/" + item.model_slug + ".webp"}
                          alt={item.label}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                          onError={(e) => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = "1"; e.target.src = "/cars-thumb/" + item.brand_slug + "/" + item.model_slug + ".webp"; } else { e.target.style.opacity = '0.3'; } }}
                        />
                      </div>
                      <span className="text-[11px] leading-tight text-slate-700 group-hover:text-orange-500 font-medium line-clamp-2">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
